import React, { useState, useCallback, useRef } from 'react';
import { Typography } from 'antd';
import useWorkflowTree from '../hooks/useWorkflowTree';
import WorkflowStepRow from './WorkflowStepRow';

const { Text } = Typography;

function buildStepMap(items, map = new Map(), counter = { n: 0 }) {
  (items || []).forEach((item) => {
    if (item.branchLabel && item.nodes) {
      buildStepMap(item.nodes, map, counter);
      return;
    }
    if (item.isBackRef) return;
    counter.n += 1;
    map.set(item.node?.id, counter.n);
    if (item.children?.length) {
      item.children.forEach((child) => {
        if (child.branchLabel) buildStepMap([child], map, counter);
        else buildStepMap(item.children, map, counter);
      });
    }
  });
  return map;
}

function renderTreeNodes(
  items,
  counter,
  stepMap,
  selectedNodeId,
  onSelect,
  onDelete,
  onDisconnectEdge,
  onInsertAfter,
  dragProps, // { draggingId, dropTargetId, dropPosition, onDragStart, onDragEnd, onDragOver, onDrop }
  matches = [],
  searchIndex = 0,
  searchQuery = ''
) {
  const rows = [];
  const { draggingId, dropTargetId, dropPosition } = dragProps;

  (items || []).forEach((item) => {
    if (item.branchLabel && item.nodes) {
      const labelClass =
        item.branchType === 'yes' ? 'flow-branch-label flow-branch-label--yes'
        : item.branchType === 'no' ? 'flow-branch-label flow-branch-label--no'
        : 'flow-branch-label flow-branch-label--route';
      const blockClass =
        item.branchType === 'yes' ? 'flow-branch-block flow-branch-block--yes'
        : item.branchType === 'no' ? 'flow-branch-block flow-branch-block--no'
        : 'flow-branch-block flow-branch-block--route';

      rows.push(
        <div key={item.branchKey} className={blockClass}>
          <div className={labelClass}>{item.branchLabel}</div>
          {renderTreeNodes(item.nodes, counter, stepMap, selectedNodeId, onSelect, onDelete, onDisconnectEdge, onInsertAfter, dragProps, matches, searchIndex, searchQuery)}
        </div>
      );
      return;
    }

    const isSearchActive = searchQuery && searchQuery.trim() !== '';
    const isMatched = isSearchActive && matches.includes(item.node?.id);
    const isCurrentMatch = isSearchActive && matches[searchIndex] === item.node?.id;
    const fade = isSearchActive && !isMatched;

    const nodeId = item.node?.id;
    const isDragging = draggingId === nodeId;
    const isDropTarget = dropTargetId === nodeId;
    const showPlaceholder = isDropTarget ? dropPosition : null;

    const stepRow = (
      <WorkflowStepRow
        key={item.key}
        item={item}
        stepNumber={counter.current + 1}
        selected={selectedNodeId === nodeId}
        isMatched={isMatched}
        isCurrentMatch={isCurrentMatch}
        fade={fade}
        onSelect={onSelect}
        onDelete={onDelete}
        onDisconnectEdge={onDisconnectEdge}
        onInsertAfter={onInsertAfter}
        isDragging={isDragging}
        showPlaceholder={showPlaceholder}
        onDragStart={dragProps.onDragStart}
        onDragEnd={dragProps.onDragEnd}
        onDragOver={dragProps.onDragOver}
        onDrop={dragProps.onDrop}
      />
    );

    if (item.isBackRef) {
      counter.current += 1;
      rows.push(stepRow);
      return;
    }

    counter.current += 1;
    rows.push(stepRow);

    if (item.children?.length) {
      item.children.forEach((child) => {
        if (child.branchLabel) {
          rows.push(...renderTreeNodes([child], counter, stepMap, selectedNodeId, onSelect, onDelete, onDisconnectEdge, onInsertAfter, dragProps, matches, searchIndex, searchQuery));
        } else {
          rows.push(
            <div key={`${child.key || child.node?.id}-seq`} className="flow-branch-block">
              {renderTreeNodes([child], counter, stepMap, selectedNodeId, onSelect, onDelete, onDisconnectEdge, onInsertAfter, dragProps, matches, searchIndex, searchQuery)}
            </div>
          );
        }
      });
    }
  });
  return rows;
}

const WorkflowListView = ({
  definition,
  selectedNodeId,
  onSelectNode,
  onDeleteNode,
  onDisconnectEdge,
  onInsertAfter,
  onMoveNode,
  searchQuery = '',
  searchIndex = 0,
  searchMatches = [],
}) => {
  const tree = useWorkflowTree(definition);
  const stepMap = buildStepMap(tree);
  const counter = { current: 0 };

  // ── Lifted drag state ────────────────────────────────────────────────
  // Use refs alongside state: refs are always current in event handlers (no stale closures)
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // 'above' | 'below'

  const draggingRef = useRef(null);
  const dropTargetIdRef = useRef(null);
  const dropPositionRef = useRef(null);

  const handleDragStart = useCallback((nodeId) => {
    draggingRef.current = nodeId;
    setDraggingId(nodeId);
    setDropTargetId(null);
    dropTargetIdRef.current = null;
    setDropPosition(null);
    dropPositionRef.current = null;
  }, []);

  const handleDragEnd = useCallback(() => {
    draggingRef.current = null;
    dropTargetIdRef.current = null;
    dropPositionRef.current = null;
    setDraggingId(null);
    setDropTargetId(null);
    setDropPosition(null);
  }, []);

  // Receives nodeId + pre-computed position string (NOT the raw event)
  // because e.currentTarget is null after React's synthetic event returns
  const handleDragOver = useCallback((nodeId, position) => {
    if (!nodeId || nodeId === draggingRef.current) {
      setDropTargetId(null);
      dropTargetIdRef.current = null;
      setDropPosition(null);
      dropPositionRef.current = null;
      return;
    }
    setDropTargetId(nodeId);
    dropTargetIdRef.current = nodeId;
    setDropPosition(position);
    dropPositionRef.current = position;
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const dragId = draggingRef.current;
    const targetId = dropTargetIdRef.current;
    const pos = dropPositionRef.current;
    handleDragEnd();
    if (!dragId || !targetId || dragId === targetId) return;
    const position = pos === 'above' ? 'before' : 'after';
    onMoveNode?.(dragId, targetId, position);
  }, [handleDragEnd, onMoveNode]);

  const dragProps = {
    draggingId,
    dropTargetId,
    dropPosition,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDragOver: handleDragOver,
    onDrop: () => {},
  };

  const rows = renderTreeNodes(
    tree, counter, stepMap, selectedNodeId,
    onSelectNode, onDeleteNode, onDisconnectEdge, onInsertAfter,
    dragProps,
    searchMatches, searchIndex, searchQuery
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <div
        className="flow-builder-workflow-scroll"
        onDragOver={(e) => {
          e.preventDefault();
          if (e.target === e.currentTarget) {
            setDropTargetId(null);
            dropTargetIdRef.current = null;
            setDropPosition(null);
            dropPositionRef.current = null;
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setDropTargetId(null);
            dropTargetIdRef.current = null;
            setDropPosition(null);
            dropPositionRef.current = null;
          }
        }}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
      >
        {rows.length === 0 ? (
          <Text type="secondary">Add a trigger node to start your flow.</Text>
        ) : rows}
      </div>
    </div>
  );
};

export default WorkflowListView;
